import React, { useMemo, useState } from "react";
import { useTable, useQueryState } from "hooks";
import { AddNewButton, Page, PendingButton, LoadingMsg } from "components/common";
import { Button, Table } from "reactstrap";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { formatCurrency } from "services/util";
import { NotificationManager } from "react-notifications";
import SettingsClient from "../SettingsClient";
import { checkSettings } from "../checkSettings";
import { ApiClient, PayrollClient } from "services";
import ImportModal from "components/ImportModal";
import { loansNotes as importantNotes } from "pages/UserList/modelNames";
import { createPortal } from "react-dom";
import { exportDownload } from "services/util";
import StatusBox from "components/StatusBox";
import { humanError } from "services/util";

const ToReview = ({ url, match, client, onApproved, onRejected, t }) => {
  console.log('to_review: ', url);
  const {
    TableContainer,
    data,
    PayrollTableDataFetch,
    BulkDelet,
    refreshData,
  } = useTable({
    url: `${url}/to_review`,
    filters: {
      approved: null,
    },
    limit: 9999,
  });

  const [pending, setPending] = useState();

  async function onApprove(i) {
    setPending(true);

    try {
      await client.patch(`${url}/${i.id}`, {
        approved: true,
      });
      NotificationManager.success(t("Loan approved"));

      refreshData();
      if (onApproved) onApproved();
    } finally {
      setPending(false);
    }
  }

  async function onReject(i) {
    setPending(true);

    try {
      await client.patch(`${url}/${i.id}`, {
        approved: false,
      });
      NotificationManager.success(t("Loan rejected"));

      refreshData();
      if (onRejected) onRejected();
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <PayrollTableDataFetch className="inv-table" />
      {data.count > 0 && (
        <>
          <hr />
          <div className="d-flex justify-content-between mb-2">
            <h4 className="font-weight-light">
              <Trans>To review</Trans>
            </h4>
            <BulkDelet
              className="d-none"
              prefix="payroll"
              organisation_data={data.results[0]}
            />
          </div>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th sortBy="employee__first_name">
                    <Trans>Employee</Trans>
                  </th>
                  <th sortBy="total_amount">
                    <Trans>Total</Trans>
                  </th>
                  <th sortBy="deduction_amount">
                    <Trans>Monthly Deduction</Trans>
                  </th>
                  <th><Trans>Approval</Trans></th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`/payroll/employees/${i.employee}`}>
                        {i.employee__full_name}
                      </Link>
                    </td>
                    <td>{i.total_amount}</td>
                    <td>{i.deduction_amount}</td>
                    <td>
                      <PendingButton pending={pending} outline className="mr-2" size="sm" >
                        <Link to={`loans/${i.id}`}><Trans>Approve</Trans></Link>
                      </PendingButton>

                      <PendingButton pending={pending} onClick={() => onReject(i)} color="danger" outline size="sm" >
                        <Trans>Reject</Trans>
                      </PendingButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
};

export const List = ({ match, url = "loans", t, payroll }) => {
  const {
    TableContainer,
    data,
    PayrollTableDataFetch,
    BulkDelet,
    TableSearch,
    ViewingRecords,
    Pagination,
    EditDeleteButtons,
    onDeleteItem,
    filters,
    refreshData,
  } = useTable({
    url,
    match,
  });

  const apiClient = ApiClient();

  const [history, setHistory] = useQueryState("history");

  const newFilters = useMemo(
    () => ({
      ...filters,
      remaining_amount__lte: history ? 0 : undefined,
      remaining_amount__gt: history ? undefined : 0,
    }),
    [filters, history]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false)

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const [settingsData, setSettings] = useState();

  const loading = !settingsData || Object.keys(settingsData).length === 0;

  const settingsCheck = checkSettings(settingsData)

  const settingsLink = () => {
    window.location.href = `/payroll/settings`;
  };

  const handleUploadFile = async (selectedFile) => {

    const formData = new FormData();
    setIsLoading(true)
    formData.append('file', selectedFile)

    payroll.post("loans/bulk_import", formData)
      .then((response) => {
        setIsLoading(false)
        NotificationManager.success(t("File imported successfully"));
        setTimeout(() => {
          setIsModalOpen(false);
          refreshData()
        }, 800);
      })
      .catch((err) => {

        setIsLoading(false);

        const error_message = humanError(err, "Error while importing File", t)
        NotificationManager.error(error_message);
        
        refreshData()
      });
  }

  return (
    <Page title="Loans" className="inv-table">
      {isLoading &&
        createPortal(
          <div className="content">
            <div className="loader"></div>
            {<LoadingMsg />}
          </div>, document.querySelector('#loader-holder')
        )}
      <SettingsClient setSettings={setSettings} />
      <ImportModal
        isOpen={isModalOpen}
        toggle={closeModal}
        title="Loans"
        importantNotes={importantNotes}
        handleUpload={handleUploadFile}
      />
      {!loading ? (
        <>
          {settingsCheck.allFieldsFilled === true ? (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <TableSearch className="my-0 flex-grow-1 mr-3" />
                <AddNewButton size="sm" resource="Loan" match={match} />
                <Button size="md" color="primary" onClick={openModal}>
                  <Trans>Import</Trans><span className="mx-1" /><i className="fas fa-file-excel" />
                </Button>
                <Button size="md" color="primary" onClick={()=>{exportDownload(payroll, "loans/export", 'loans.xlsx')}}>
                  <Trans>Export </Trans><span className="mx-1" /><i className="fas fa-file-excel" /> 
                </Button>
              </div>
              <ToReview
                url={url}
                match={match}
                t={t}
                client={payroll}
                onApproved={refreshData}
              />
              <hr />
              <div className="d-flex justify-content-between mb-1 mt-2">
                <h4 className="font-weight-light">
                  <Trans>{history ? "History" : "Current"}</Trans>
                </h4>
                <PendingButton
                  color="link"
                  className="p-0"
                  onClick={() => {
                    setHistory(history ? undefined : true);
                  }}
                >
                  View {history ? "Current" : "History"}
                </PendingButton>
              </div>
              <BulkDelet className="d-none" />
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th sortBy="employee__first_name">
                        <Trans>Employee</Trans>
                      </th>
                      <th sortBy="description">
                        <Trans>Description</Trans>
                      </th>
                      <th sortBy="total_amount">
                        <Trans>Total</Trans>
                      </th>
                      <th sortBy="deduction_amount">
                        <Trans>Monthly Deduction</Trans>
                      </th>
                      <th sortBy="calculated_status">
                        <Trans>Status</Trans>
                      </th>
                      <th sortBy="remaining_amount">
                        <Trans>Remaining</Trans>
                      </th>
                      <th><Trans>Actions</Trans></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results
                      .filter((i) => (history ? true : (i.calculated_status === 'Running' || i.calculated_status === 'Approved')))
                      .map((i) => (
                        <tr key={i.id}>
                          <td>
                            <Link to={`/payroll/loans/${i.id}`}>
                              {i.employee__full_name}
                            </Link>
                          </td>
                          <td>{i.description}</td>
                          <td>{formatCurrency(i.total_amount)}</td>
                          <td>{formatCurrency(i.deduction_amount)}</td>
                          <td><StatusBox status={i.calculated_status} /></td>
                          <td>{formatCurrency(i.remaining_amount)}</td>
                          <td className="text-end text-nowrap">
                            <EditDeleteButtons
                              url={match.url}
                              id={i.id}
                              onDelete={onDeleteItem}
                              showDelete={!i.approved}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </TableContainer>
              <PayrollTableDataFetch filters={newFilters} />
              <div className="d-flex justify-content-between">
                <div className=""><ViewingRecords noCount /></div>
                <div className=""><Pagination /></div>
              </div>
            </>
          ) : (
            <div className="alert alert-danger fade show my-3">
              <ul className="mb-0 pl-3 text-center">
                <span style={{ cursor: "pointer" }} onClick={settingsLink}><Trans>Please Setup Payroll Accounts and It's Settings to Create/Approve Loans.</Trans></span>
              </ul>
            </div>
          )}
        </>) :
        (<LoadingMsg />)}
    </Page>
  );
};


export default List;
